import { Button } from './Button';
import { Modal } from './Modal';

/**
 * An image, shown at the size the screen allows.
 *
 * **It writes nothing, so it carries no confirm tick.** That rule gates the primary action of
 * a *transaction*; this modal has no primary action at all — Close is the only control, and a
 * tick in front of "look at a picture" is ceremony that teaches readers to click past ticks.
 *
 * The caller owns where the URL came from. For a person's photograph that is an object URL
 * out of the identity store, which is meaningless outside this tab — so this component never
 * offers a download or an "open in new tab", both of which would be dead links.
 */
export function ImagePreviewModal({
  open,
  url,
  title,
  alt,
  onClose,
  id = 'image-preview',
}: {
  open: boolean;
  /** Null renders nothing: there is no preview of an image that is not there. */
  url: string | null;
  title: string;
  /**
   * What the image shows, for a reader who cannot see it. Required rather than optional —
   * an empty `alt` here would make the modal's only content invisible to a screen reader,
   * which is the opposite of the decorative case `alt=""` is for.
   */
  alt: string;
  onClose: () => void;
  id?: string;
}) {
  if (!open || !url) return null;

  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="lg"
      id={`${id}-modal`}
      footer={
        <Button id={`${id}-close`} variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      {/*
        Contained, never cropped: this is the one place the whole picture matters, so it is
        `object-contain` on a tinted ground rather than the `object-cover` circle everywhere
        else. The cap is on the VIEWPORT height, so a portrait photograph cannot grow the
        modal until its own footer scrolls away.
      */}
      <div className="flex items-center justify-center rounded-lg bg-slate-100 p-3">
        <img
          src={url}
          alt={alt}
          className="max-h-[60vh] w-auto max-w-full rounded object-contain"
        />
      </div>
    </Modal>
  );
}
